import { useFormikContext } from "formik"
import { useEffect, useRef } from "react"
import { useIsSubmissionDisabled } from "../SubmitButton"

// Submits formik form when Meta+Enter keyboard keys are pressed.
// NOTE: if Formik is not provided the onSubmit prop, using this
// component will result in an unhandled error when user uses
// keyboard shortcut.
function KeyboardFormSubmit() {
  const ref = useRef<HTMLDivElement>(null)
  const context = useFormikContext()
  const isSubmissionDisabled = useIsSubmissionDisabled()

  useEffect(() => {
    function handler(e: KeyboardEvent) {
      // The "DOM anchor" for this instance of KeyboardFormSubmit must
      // be contained by the closest form in order to handle the event.
      // This handles the case where multiple forms exist in the DOM
      // but we only want to submit the one in which the user executed
      // the keyboard shortcut.
      const closestFormContainsTarget = !!ref.current
        ?.closest?.("form")
        ?.contains(e.target as Node)

      if (
        e.key === "Enter" &&
        e.getModifierState("Meta") &&
        closestFormContainsTarget
      ) {
        try {
          context.submitForm?.()
        } catch (error) {
          console.warn(
            "Failed attempt to submit form via keyboard shortcut due to error:",
            error
          )
        }
      }
    }

    // Only enable handler when form can be submitted if form submission is enabled.
    if (!isSubmissionDisabled) {
      document.addEventListener("keydown", handler)

      return () => {
        document.removeEventListener("keydown", handler)
      }
    }
  }, [isSubmissionDisabled])

  // Render a dummy element which we can use as a DOM anchor
  // to calibrate our submit handler to.
  return <div ref={ref} />
}

export default KeyboardFormSubmit
